/* eslint-disable arrow-body-style */
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {inject} from "@angular/core";
import {catchError, concatMap, exhaustMap, forkJoin, map, Observable, of, switchMap, tap} from "rxjs";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ErrorHandlingUtil} from "../../utils/error-handling-util";
import {UserSettingService} from "../../services/user-setting/user-setting.service";
import {SaveSettingsPayload, UserSettingActions} from "./user-setting.actions";

export const loadDefinitions = createEffect(
    (actions$ = inject(Actions), service = inject(UserSettingService)) => {
        return actions$.pipe(
            ofType(UserSettingActions.loadDefinitions),
            switchMap(() =>
                service.getSettingDefinitions().pipe(
                    map((states) => UserSettingActions.loadDefinitionsSuccess({states})),
                    catchError((errorResponse: any) => of(UserSettingActions.loadDefinitionsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const saveSettings = createEffect(
    (actions$ = inject(Actions), service = inject(UserSettingService)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveSettings),
            // concatMap (not exhaustMap): queue successive saves so a second save dispatched while
            // the first is in flight still runs, instead of being silently dropped.
            //
            // NOT atomic: the writes run together via forkJoin, so a partial failure leaves the
            // already-succeeded writes committed on the server while we emit saveSettingsError and skip
            // the post-save reload. The form keeps the attempted (dirty) values, so the user simply
            // retries; the next successful save (or an app reload) reconciles. Acceptable because these
            // are independent per-key settings with no cross-key invariant.
            concatMap((action: SaveSettingsPayload) => {
                const operations: Observable<void>[] = [
                    ...action.userDeletes.map((key) => service.deleteUserSetting(key)),
                    ...action.userSets.map(({key, value}) => service.saveUserSetting(key, value)),
                    ...action.adminSets.map(({key, value}) => service.saveAdminSetting(key, value)),
                    ...action.lockSets.map(({key, overridable}) => service.saveAdminLock(key, overridable))
                ];
                const operations$: Observable<unknown> = operations.length ? forkJoin(operations) : of(null);
                return operations$.pipe(
                    map(() => UserSettingActions.saveSettingsSuccess()),
                    catchError((errorResponse: any) => of(UserSettingActions.saveSettingsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                );
            })
        );
    },
    {functional: true}
);

export const reloadDefinitionsAfterSave = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveSettingsSuccess),
            map(() => UserSettingActions.loadDefinitions())
        );
    },
    {functional: true}
);

// The flat merged map (settings.data) is consumed outside the settings page (e.g. the sensitivity
// compute dialogs and the new-deal currency default). saveSettings only refreshes the definitions
// slice, so reload the merged map too, otherwise those consumers read stale values until the next
// app bootstrap.
export const reloadMergedSettingsAfterSave = createEffect(
    (actions$ = inject(Actions)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveSettingsSuccess),
            map(() => UserSettingActions.loadAll())
        );
    },
    {functional: true}
);

export const showSaveSettingsSuccess = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveSettingsSuccess),
            tap(() => {
                snackBar.open("Settings saved successfully.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showSaveSettingsError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveSettingsError),
            tap(() => {
                snackBar.open("Settings could not be saved.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const loadAllSettings = createEffect(
    (actions$ = inject(Actions), service = inject(UserSettingService)) => {
        return actions$.pipe(
            ofType(UserSettingActions.loadAll),
            switchMap(() =>
                service.getMergedSettings().pipe(
                    map((settings) => UserSettingActions.loadAllSuccess({settings})),
                    catchError((errorResponse: any) => of(UserSettingActions.loadAllError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

// The merged map backs off-page consumers (sensitivity dialogs, new-deal currency default) and is
// reloaded after every save; surface a failure so a stale read isn't silent.
export const showLoadAllError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(UserSettingActions.loadAllError),
            tap(() => {
                snackBar.open("Settings could not be loaded.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const saveTableSettings = createEffect(
    (actions$ = inject(Actions), service = inject(UserSettingService)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveTableSettings),
            exhaustMap((action) =>
                service.saveTableSettings(action.tableKey, action.columns, action.pageSize, action.sort).pipe(
                    map((settings) => UserSettingActions.saveTableSettingsSuccess({settings})),
                    catchError((errorResponse: any) => of(UserSettingActions.saveTableSettingsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const saveAdminTableSettings = createEffect(
    (actions$ = inject(Actions), service = inject(UserSettingService)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveAdminTableSettings),
            exhaustMap((action) =>
                service.saveAdminTableSettings(action.tableKey, action.columns, action.pageSize, action.sort).pipe(
                    switchMap(() => service.getMergedSettings()),
                    map((settings) => UserSettingActions.saveAdminTableSettingsSuccess({settings})),
                    catchError((errorResponse: any) => of(UserSettingActions.saveAdminTableSettingsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);

export const showSaveError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveTableSettingsError),
            tap(() => {
                snackBar.open("Settings could not be saved.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const showSaveAdminError = createEffect(
    (actions$ = inject(Actions), snackBar = inject(MatSnackBar)) => {
        return actions$.pipe(
            ofType(UserSettingActions.saveAdminTableSettingsError),
            tap(() => {
                snackBar.open("Settings could not be saved.", "", {duration: 3000});
            })
        );
    },
    {functional: true, dispatch: false}
);

export const resetTableSettings = createEffect(
    (actions$ = inject(Actions), service = inject(UserSettingService)) => {
        return actions$.pipe(
            ofType(UserSettingActions.resetTableSettings),
            exhaustMap((action) =>
                service.deleteTableSettings(action.tableKey).pipe(
                    map((settings) => UserSettingActions.resetTableSettingsSuccess({settings})),
                    catchError((errorResponse: any) => of(UserSettingActions.resetTableSettingsError({errorMsg: ErrorHandlingUtil.getErrorMessage(errorResponse)})))
                )
            )
        );
    },
    {functional: true}
);
